import React from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { Check, Close, LockOutlined } from '@mui/icons-material';
import {
  alpha,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';

import useMyRotraTranslations, {
  GeneralKeys,
} from '$utils/useMyRotraTranslations';

const StyledTextfield = styled(TextField)(({ theme }) => ({
  '&[data-disabled="true"]': {
    background: theme.palette.background.default,
  },
  [`&[data-completed="true"]`]: {
    cursor: 'pointer !important',
    '*': {
      cursor: 'pointer !important',
    },
    '& fieldset': {
      borderColor: `${alpha(theme.palette.common.black, 0.23)} !important`,
    },
  },
  [`&[data-completed="false"]`]: {
    cursor: 'pointer !important',
    '*': {
      cursor: 'pointer !important',
    },
  },
  '&:hover [data-action="delete"]': {
    opacity: 1,
  },
  '&:hover [data-action="check"]': {
    opacity: 0,
  },
  '&:hover button': {
    background: alpha(theme.palette.common.black, 0.08),
  },
  '&:hover button:hover': {
    background: alpha(theme.palette.common.black, 0.12),
  },
}));

const StyledIconButton = styled(IconButton)(() => ({
  position: 'relative',
  width: 28,
  height: 28,
  '& > svg': {
    position: 'absolute',
    fontSize: 20,
    transition: 'all 0.25s ease-in-out',
  },
  '[data-action="delete"]': {
    opacity: 0,
  },
  '[data-action="check"]': {
    opacity: 1,
  },
}));

interface RotraTextFieldProps {
  fieldPath: string;
  showErrorMessage?: boolean;
  maxLength?: number;
  minLength?: number;
  showMaxLengthIndicator?: boolean;
  forceErrorState?: boolean;
}

const normalizeToRenderable = (val: unknown): React.ReactNode => {
  if (typeof val === 'string' || React.isValidElement(val)) {
    return val;
  }
  if (val == null) {
    return '';
  }

  try {
    return JSON.stringify(val);
  } catch {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- Last-resort display when JSON serialization fails (for example, a circular value).
    return String(val);
  }
};

export default function RotraTextFieldV2(
  props: RotraTextFieldProps & TextFieldProps
) {
  const {
    fieldPath,
    showErrorMessage = true,
    type,
    helperText,
    margin,
    slotProps,
    maxLength = Number.MAX_SAFE_INTEGER,
    minLength = 0,
    showMaxLengthIndicator,
    forceErrorState,
    ...rest
  } = props;

  const { control } = useFormContext();
  const { tShared } = useMyRotraTranslations({});

  const resolveErrorMessage = (error?: FieldError): React.ReactNode => {
    if (!error) {
      return undefined;
    }

    const raw: unknown =
      error.message ?? (error as FieldError & { key?: unknown }).key ?? null;
    if (!raw) {
      return undefined;
    }

    let text: unknown = raw;

    if (raw && typeof raw === 'object' && 'key' in raw) {
      const { key, values } = raw as {
        key: GeneralKeys;
        values?: Record<string, unknown>;
      };

      try {
        text = tShared(key, values);
      } catch (error_) {
        console.error('RotraTextFieldV2 tShared error', {
          fieldPath,
          key,
          values,
          error: error_,
        });
        text = key;
      }
    }

    return normalizeToRenderable(text);
  };

  const getHelperText = (
    value: string | null | undefined,
    error?: FieldError
  ): React.ReactNode => {
    if (
      ((showMaxLengthIndicator && !error) || error?.type === 'max') &&
      (value?.length || 0) !== 0
    ) {
      return `${value?.length || 0}/${
        maxLength || 'maxLength property is undefined'
      }`;
    }

    const errorText = resolveErrorMessage(error);
    if (errorText) {
      return errorText;
    }

    return normalizeToRenderable(helperText);
  };

  return (
    <Controller
      name={fieldPath}
      control={control}
      render={({ field: { value, onChange, ...restField }, fieldState }) => {
        const customOnChangeLogic = (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          const val = event.target.value;

          if (type === 'number') {
            if (val.trim() === '') {
              onChange(null);
            } else if (val.length <= maxLength && val.length >= minLength) {
              onChange(val);
            }
          } else {
            onChange(val);
          }
        };

        const safeHelperText = getHelperText(
          value as string | null | undefined,
          showErrorMessage ? fieldState.error : undefined
        );

        return (
          <StyledTextfield
            data-completed={!!value && !fieldState.error}
            data-disabled={rest.disabled ? 'true' : 'false'}
            sx={{
              marginBottom: (theme) =>
                margin === 'none' ? 0 : theme.spacing(1),
              ...rest.sx,
            }}
            margin={margin}
            value={value ?? ''}
            {...restField}
            {...rest}
            type={type}
            error={showErrorMessage && (forceErrorState || fieldState.invalid)}
            helperText={safeHelperText}
            onChange={customOnChangeLogic}
            fullWidth
            slotProps={{
              ...slotProps,
              formHelperText: {
                style: {
                  textAlign:
                    showMaxLengthIndicator &&
                    typeof value === 'string' &&
                    value.length > 0 &&
                    (!fieldState.error || fieldState.error.type === 'max')
                      ? 'right'
                      : 'left',
                },
              },
              htmlInput: {
                ...slotProps?.htmlInput,
                autoComplete: 'new-password',
              },
              input: {
                ...slotProps?.input,
                endAdornment: (
                  <InputAdornment position="end">
                    {rest.disabled ? (
                      <Tooltip
                        title="Disabled because the form is read-only"
                        placement="top"
                        arrow
                      >
                        <LockOutlined />
                      </Tooltip>
                    ) : (
                      <StyledIconButton
                        tabIndex={-1}
                        size="small"
                        edge="end"
                        onClick={() => {
                          onChange('');
                        }}
                        sx={{
                          visibility:
                            value && !fieldState.error ? 'visible' : 'hidden',
                        }}
                      >
                        <Close data-action="delete" />
                        <Check data-action="check" />
                      </StyledIconButton>
                    )}
                  </InputAdornment>
                ),
              },
            }}
          />
        );
      }}
    />
  );
}
